import useMessageSearch from "@/hooks/useMessageSearch"
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ArrowLeft, CalendarDays, X } from "lucide-react"
import { format } from "date-fns"
import { Trans, useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { XmppContext } from "@/stream/xmppClient"
import { SearchHit } from "@/lib/searchResultParser"
import { extractVhostFromJid } from "@/jidUtils"
import { useAppDispatch, useAppSelector, useAppStore } from "@/reducers/hooks"
import { selectChat } from "@/reducers/chatsSliceSelectors"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { hideImageGallery, hidePDFViewer } from "@/reducers/overlaysSlice"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import Loader from "@/components/Loader"
import SearchMessageDetails from "./SearchMessageDetails"
import SearchMessageRow from "./SearchMessageRow"
import SearchPopoverBar from "./SearchPopoverBar"

export default function SearchPopover({
  isOpen,
  onClose,
  chatJid,
}: {
  isOpen: boolean
  onClose: () => void
  chatJid?: string
}) {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const showVhost = myJid.getDomain() === "slickshift.ai"
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const mediaSource = `message-search-${useId()}`
  const gallery = useAppSelector((state) => state.overlays.imageGallery)
  const pdf = useAppSelector((state) => state.overlays.PDFViewer)
  const ownsGallery = gallery.visible && gallery.externalSource === mediaSource
  const ownsPdf = pdf.visible && pdf.externalSource === mediaSource
  const chat = useAppSelector(selectChat(chatJid ?? ""))
  const accountChat = useAppSelector(selectAccountChatByJid(chatJid ?? ""))
  const [searchQuery, setSearchQuery] = useState("")
  const [detailsShownFor, setDetailsShownFor] = useState<SearchHit>()
  const [savedScroll, setSavedScroll] = useState<number>()
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | null>(
    null,
  )
  const [dialogElement, setDialogElement] = useState<HTMLDivElement | null>(
    null,
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const resultsScrolled = useRef(false)
  const {
    hits,
    hasMoreResults,
    loading,
    loadingMore,
    error,
    search,
    loadMore,
    cancel,
  } = useMessageSearch({
    client,
    myJid,
    isOpen,
    chatJid,
    searchQuery,
    dateRange,
  })
  const chatName = accountChat?.name || chat?.name || hits[0]?.document.chat
  const titleChatJid = detailsShownFor?.document.chat_jid || chatJid

  useEffect(
    () => () => {
      const overlays = store.getState().overlays
      if (overlays.imageGallery.externalSource === mediaSource)
        dispatch(hideImageGallery())
      if (overlays.PDFViewer.externalSource === mediaSource)
        dispatch(hidePDFViewer())
    },
    [dispatch, mediaSource, store],
  )

  useLayoutEffect(() => {
    if (!detailsShownFor && savedScroll !== undefined)
      scrollRef.current?.scrollTo(0, savedScroll)
  }, [detailsShownFor, savedScroll])

  useEffect(() => {
    resultsScrolled.current = false
  }, [search])

  const checkMoreResults = useCallback(() => {
    const viewport = scrollRef.current
    if (!viewport || detailsShownFor || error) return
    // Wait for a real scroll, unless the results fit without one: then the user
    // could never reach the end, so load the next page straight away.
    const canScroll = viewport.scrollHeight > viewport.clientHeight
    if (canScroll && !resultsScrolled.current) return
    if (
      viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <
      240
    )
      void loadMore()
  }, [loadMore, detailsShownFor, error])

  useEffect(() => {
    if (loadingMore || loading) return
    const frame = requestAnimationFrame(checkMoreResults)
    return () => cancelAnimationFrame(frame)
  }, [loadingMore, loading, hits, checkMoreResults])

  const close = () => {
    cancel()
    if (ownsGallery) dispatch(hideImageGallery())
    if (ownsPdf) dispatch(hidePDFViewer())
    setDetailsShownFor(undefined)
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close()
      }}
    >
      <DialogContent
        ref={setDialogElement}
        id="message-search-dialog"
        showCloseButton={false}
        animation="fade"
        style={{ transform: "none" }}
        className="bottom-0 left-0 right-0 top-0 m-auto flex h-dvh w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[90dvh] sm:w-[calc(100vw-3rem)] sm:max-w-6xl sm:rounded-xl"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          searchInputRef.current?.focus()
        }}
        onEscapeKeyDown={(event) => {
          if (ownsGallery || ownsPdf) {
            event.preventDefault()
            if (ownsGallery) dispatch(hideImageGallery())
            if (ownsPdf) dispatch(hidePDFViewer())
          }
        }}
        onInteractOutside={(event) => {
          if (ownsGallery || ownsPdf) event.preventDefault()
        }}
      >
        <header
          className="relative shrink-0 border-b bg-muted/30 px-4 py-4 sm:px-6"
          data-testid="search-popover"
        >
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("app.search.close")}
            data-testid="search-close-button"
            onClick={close}
            className="absolute right-3 top-3 text-muted-foreground"
          >
            <X className="size-4" />
          </Button>
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 pr-8">
            {detailsShownFor && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDetailsShownFor(undefined)}
                className="-ml-2 w-fit"
              >
                <ArrowLeft className="size-4" />
                {t("app.search.backToResults")}
              </Button>
            )}
            <DialogTitle className={detailsShownFor ? "text-xl" : "text-base"}>
              {detailsShownFor?.document.chat ||
                (chatJid
                  ? chatName || t("app.search.inThisChat")
                  : t("app.search.inMessages"))}
              {showVhost && titleChatJid && (
                <span className="ml-2 break-all text-sm font-normal text-muted-foreground">
                  {extractVhostFromJid(titleChatJid)}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("app.search.description")}
            </DialogDescription>
            {!detailsShownFor && (
              <>
                <SearchPopoverBar
                  inputRef={searchInputRef}
                  onSearch={() => void search()}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <DateRangePicker
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    maxDays={365}
                    popoverClassName="z-[1100] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
                    portalContainer={dialogElement ?? undefined}
                    align="start"
                    showClearButton
                  />
                </div>
              </>
            )}
            {dateRange && (
              <p
                className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
                data-testid="search-period"
              >
                <CalendarDays className="size-3.5" />
                <Trans
                  i18nKey="app.search.period"
                  values={{
                    from: format(
                      dateRange.from,
                      t("app.search.periodDateFormat"),
                      {
                        locale: getDateFnsLocale(),
                      },
                    ),
                    to: format(dateRange.to, t("app.search.periodDateFormat"), {
                      locale: getDateFnsLocale(),
                    }),
                  }}
                  components={{
                    b: <span className="font-medium text-foreground" />,
                  }}
                />
              </p>
            )}
          </div>
        </header>
        {detailsShownFor ? (
          <SearchMessageDetails
            key={`${detailsShownFor.document.chat_jid}:${detailsShownFor.document.id}`}
            clickedMessageHit={detailsShownFor}
            mediaSource={mediaSource}
            portalContainer={dialogElement ?? undefined}
          />
        ) : (
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto bg-background"
            data-testid="search-results"
            tabIndex={0}
            onScroll={() => {
              resultsScrolled.current = true
              checkMoreResults()
            }}
          >
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-4 py-5 sm:px-0">
              {loading ? (
                <div
                  role="status"
                  aria-label={t("app.search.searching")}
                  className="flex justify-center py-10"
                >
                  <Loader />
                </div>
              ) : (
                <>
                  {hits.length > 0 && (
                    <p className="text-sm font-medium">
                      {t("app.search.bestMatches")}
                    </p>
                  )}
                  {hits.map((hit) => (
                    <SearchMessageRow
                      key={hit.document.id}
                      messageHit={hit}
                      showChatTitle={!chatJid}
                      showVhost={showVhost}
                      onGoToDetails={() => {
                        window.analytics.track("SearchMessageClicked", {
                          userJid: myJid.toString(),
                          messageId: hit.document.id,
                        })
                        setSavedScroll(scrollRef.current?.scrollTop)
                        setDetailsShownFor(hit)
                      }}
                    />
                  ))}
                  {!hits.length && !error && (
                    <p className="py-10 text-center text-sm text-muted-foreground">
                      {searchQuery.trim()
                        ? t("app.search.noMessages")
                        : t("app.search.enterPhrase")}
                    </p>
                  )}
                </>
              )}
              {error && (
                <div
                  role="alert"
                  className="text-center text-sm text-destructive"
                >
                  <p>{error}</p>
                  <Button
                    variant="ghost"
                    onClick={() => void (hits.length ? loadMore() : search())}
                  >
                    {t("app.actions.tryAgain")}
                  </Button>
                </div>
              )}
              {loadingMore && !loading && (
                <div className="flex justify-center py-2">
                  <div role="status" aria-label={t("app.search.loadingMore")}>
                    <Loader small />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
